/**
 * Authenticated API client for the clinic's own screens.
 *
 * The `aletheias_auth_token` key is shared with every other Vetware app, which is
 * what gives single sign-on across products. Do not rename it here.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api'
const TOKEN_KEY = 'aletheias_auth_token'

export class ApiError extends Error {
  status: number
  detail: any
  constructor(status: number, detail: any) {
    const message =
      typeof detail === 'string' ? detail : detail?.reason || `API error ${status}`
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}
export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}
export function clearAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

function handleUnauthorized(): void {
  clearAuthToken()
  const here = window.location.pathname + window.location.search
  window.location.assign(`/login?redirect=${encodeURIComponent(here)}`)
}

export async function apiClient<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = getAuthToken()
  const response = await fetch(`${API_BASE}/vetreviews${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  })
  if (response.status === 401) {
    handleUnauthorized()
    throw new Error('Session expired — redirecting to login')
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new ApiError(response.status, error.detail)
  }
  return response.json()
}

/** `/api/auth/*` is not under the product prefix. */
export async function authFetch<T = any>(path: string, body?: unknown): Promise<T> {
  const token = getAuthToken()
  const response = await fetch(`${API_BASE}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new ApiError(response.status, error.detail)
  }
  // /auth/verify-email answers 204 with no body, and response.json() throws on
  // an empty one -- a success that would surface to the user as a parse error.
  if (response.status === 204) return undefined as T
  return response.json()
}

// Deliberately NOT authenticated, and not routed through handleUnauthorized():
// the whole point of these three is that the caller has no usable session. A
// 401/403 here is a real answer about the token in the URL, not a stale login.
export const forgotPassword = (email: string) =>
  authFetch<{ status: string }>('/auth/forgot-password', { email })
export const resetPassword = (token: string, password: string) =>
  authFetch<{ access_token: string }>('/auth/reset-password', { token, password })
export const verifyEmail = (token: string) => authFetch<void>('/auth/verify-email', { token })

/** Mirrors ResetPasswordIn.password on the gateway: Field(min_length=10). */
export const MIN_PASSWORD_LENGTH = 10

// ---- queue -----------------------------------------------------------------
export const fetchSummary = () => apiClient('/summary')
export const fetchResponses = (q: Record<string, any> = {}) => {
  const p = new URLSearchParams()
  Object.entries(q).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  })
  const qs = p.toString()
  return apiClient(`/responses${qs ? `?${qs}` : ''}`)
}
export const fetchResponse = (id: string) => apiClient(`/responses/${id}`)
export const acknowledgeResponse = (id: string) =>
  apiClient(`/responses/${id}/acknowledge`, { method: 'POST' })
export const setFollowup = (id: string, q: Record<string, any>) => {
  const p = new URLSearchParams()
  Object.entries(q).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  })
  return apiClient(`/responses/${id}/followup?${p.toString()}`, { method: 'PATCH' })
}

// ---- dashboard -------------------------------------------------------------
export const fetchDashboard = (q: Record<string, any> = {}) => {
  const p = new URLSearchParams()
  Object.entries(q).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  })
  const qs = p.toString()
  return apiClient(`/dashboard${qs ? `?${qs}` : ''}`)
}

// ---- settings and content --------------------------------------------------
export const fetchSettings = () => apiClient('/settings')
export const patchSettings = (q: Record<string, any>) => {
  const p = new URLSearchParams()
  Object.entries(q).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  })
  return apiClient(`/settings?${p.toString()}`, { method: 'PATCH' })
}
export const rotateSlug = () => apiClient('/settings/rotate-slug', { method: 'POST' })
export const fetchStaff = (includeInactive = false) =>
  apiClient(`/staff?include_inactive=${includeInactive}`)
export const addStaff = (name: string, role?: string) =>
  apiClient(
    `/staff?display_name=${encodeURIComponent(name)}` +
      (role ? `&role_label=${encodeURIComponent(role)}` : ''),
    { method: 'POST' },
  )
export const archiveStaff = (id: string) => apiClient(`/staff/${id}`, { method: 'DELETE' })
export const restoreStaff = (id: string) =>
  apiClient(`/staff/${id}?active=true`, { method: 'PATCH' })
export const uploadStaffPhoto = async (id: string, file: File) => {
  const fd = new FormData()
  fd.append('file', file)
  const token = getAuthToken()
  const r = await fetch(`${API_BASE}/vetreviews/staff/${id}/photo`, {
    method: 'PUT',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  })
  if (!r.ok) {
    const e = await r.json().catch(() => ({ detail: r.statusText }))
    throw new ApiError(r.status, e.detail)
  }
  return r.json()
}
export const fetchUsers = () => apiClient('/users')
export const fetchCharities = (includeInactive = false) =>
  apiClient(`/charities?include_inactive=${includeInactive}`)
export const addCharity = (name: string, url?: string) =>
  apiClient(
    `/charities?name=${encodeURIComponent(name)}` + (url ? `&url=${encodeURIComponent(url)}` : ''),
    { method: 'POST' },
  )
export const archiveCharity = (id: string) =>
  apiClient(`/charities/${id}`, { method: 'DELETE' })
export const restoreCharity = (id: string) =>
  apiClient(`/charities/${id}?active=true`, { method: 'PATCH' })
export const addDestination = (platform: string, url: string) =>
  apiClient(
    `/review-destinations?platform=${platform}&url=${encodeURIComponent(url)}`,
    { method: 'POST' },
  )
export const removeDestination = (id: string) =>
  apiClient(`/review-destinations/${id}`, { method: 'DELETE' })
export const addLead = (userId: string) => apiClient(`/leads/${userId}`, { method: 'POST' })
export const removeLead = (userId: string) => apiClient(`/leads/${userId}`, { method: 'DELETE' })

// ---- survey builder --------------------------------------------------------
export const fetchTemplate = () => apiClient('/template')
export const saveTemplateDraft = (questions: any[]) =>
  apiClient('/template', { method: 'PUT', body: JSON.stringify({ questions }) })
export const publishTemplate = () => apiClient('/template/publish', { method: 'POST' })
export const discardTemplateDraft = () => apiClient('/template/draft', { method: 'DELETE' })
export const createQuestion = (q: any) =>
  apiClient('/questions', { method: 'POST', body: JSON.stringify(q) })
export const updateQuestion = (code: string, q: any) =>
  apiClient(`/questions/${code}`, { method: 'PATCH', body: JSON.stringify(q) })
export const archiveQuestion = (code: string) =>
  apiClient(`/questions/${code}`, { method: 'DELETE' })
